import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, User, DollarSign, MapPin, Building2,
  TrendingUp, AlertCircle, Share2, Image as ImageIcon,
  Info, HelpCircle, FileText, ShieldAlert, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import ExplainButton from '../components/ExplainButton';
import ResponsibilityNotice from '../components/ResponsibilityNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const brl = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
    : '—';

const AREA_META = {
  saude:          { label: 'Saúde',              emoji: '🏥' },
  educacao:       { label: 'Educação',           emoji: '🎓' },
  infraestrutura: { label: 'Infraestrutura',     emoji: '🏗️' },
  esporte:        { label: 'Esporte',            emoji: '⚽' },
  meio_ambiente:  { label: 'Meio Ambiente',      emoji: '🌱' },
  cultura:        { label: 'Cultura',            emoji: '🎭' },
  assistencia:    { label: 'Assistência Social', emoji: '👥' },
  agropecuaria:   { label: 'Agropecuária',       emoji: '🚜' },
  seguranca:      { label: 'Segurança',          emoji: '🛡️' },
  outros:         { label: 'Outros',             emoji: '📌' },
};

const Glossario = () => (
  <details className="border border-[#E2E8F0] rounded-lg p-3 bg-white" data-testid="emendas-glossario">
    <summary className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-[#006030]">
      <HelpCircle className="w-4 h-4" /> Glossário. O que significa cada termo?
    </summary>
    <dl className="mt-3 space-y-2 text-xs text-[#475569]">
      <div><dt className="font-bold text-[#0F1C2E]">Emenda individual</dt><dd>Recurso que cada parlamentar destina anualmente, sem precisar de aprovação do plenário.</dd></div>
      <div><dt className="font-bold text-[#0F1C2E]">Emenda de bancada</dt><dd>Destinada por um grupo de parlamentares do mesmo estado.</dd></div>
      <div><dt className="font-bold text-[#0F1C2E]">Valor empenhado</dt><dd>O governo se comprometeu a pagar esse valor.</dd></div>
      <div><dt className="font-bold text-[#0F1C2E]">Valor liquidado</dt><dd>O serviço foi entregue e o valor confirmado.</dd></div>
      <div><dt className="font-bold text-[#0F1C2E]">Valor pago</dt><dd>O dinheiro efetivamente saiu do cofre público para o beneficiário.</dd></div>
      <div><dt className="font-bold text-[#0F1C2E]">Favorecido</dt><dd>Quem recebeu o recurso. Pode ser prefeitura, hospital, ONG, escola.</dd></div>
    </dl>
  </details>
);

const HonestEmptyState = ({ parl, message }) => (
  <Card className="p-6 border-2 border-[#F59E0B] bg-gradient-to-br from-[#FEF3C7] to-[#FEF9C3]" data-testid="parlamentar-empty-state">
    <div className="flex items-start gap-3">
      <ShieldAlert className="w-8 h-8 text-[#B45309] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-black text-[#78350F] leading-tight mb-2">
          Dados de emendas por parlamentar ainda não estão disponíveis
        </h2>
        <p className="text-sm text-[#78350F] leading-relaxed mb-3">
          {message || (
            <>
              Os dados de emendas nominais por parlamentar são fornecidos pelo{' '}
              <strong>Portal da Transparência do Governo Federal</strong>, que exige uma chave de acesso oficial.
              Estamos em processo de solicitação.
            </>
          )}
        </p>
        <div className="bg-white/70 rounded-lg p-3 border border-[#FCD34D] mb-3">
          <p className="text-xs font-bold text-[#78350F] mb-2 uppercase tracking-wider">
            Compromisso com a verdade
          </p>
          <p className="text-xs text-[#78350F] leading-relaxed">
            Preferimos não mostrar nada a mostrar dados falsos ou aproximações que possam confundir você.
            Assim que o acesso for liberado, você verá o histórico real de emendas de {parl?.nome || 'cada parlamentar'} aqui.
          </p>
        </div>
      </div>
    </div>
  </Card>
);

const AlternativasCTA = () => (
  <Card className="p-5 border border-[#006030]/30 bg-[#F0FDF4]" data-testid="parlamentar-cta-alternativa">
    <h3 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
      Mas você pode investigar de outra forma
    </h3>
    <p className="text-sm text-[#475569] mb-4 leading-relaxed">
      Enquanto liberamos os dados por parlamentar, você já pode ver <strong>quem recebeu dinheiro público</strong> em municípios e instituições. Esses dados são <strong>100% reais</strong> e vêm do Tesouro Transparente.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Link
        to="/emendas"
        className="flex items-start gap-3 p-3 rounded-lg bg-white border border-[#006030]/30 hover:border-[#006030] hover:shadow-md transition-all"
        data-testid="cta-buscar-municipio"
      >
        <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
          <MapPin className="w-4 h-4 text-[#006030]" />
        </div>
        <div className="min-w-0">
          <p className="font-bold text-sm text-[#0F1C2E]">Buscar por município</p>
          <p className="text-xs text-[#475569] mt-0.5">Veja quanto sua cidade recebeu, quando e de quem.</p>
        </div>
      </Link>
      <Link
        to="/empresa"
        className="flex items-start gap-3 p-3 rounded-lg bg-white border border-[#006030]/30 hover:border-[#006030] hover:shadow-md transition-all"
        data-testid="cta-buscar-empresa"
      >
        <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
          <Building2 className="w-4 h-4 text-[#006030]" />
        </div>
        <div className="min-w-0">
          <p className="font-bold text-sm text-[#0F1C2E]">Buscar por instituição</p>
          <p className="text-xs text-[#475569] mt-0.5">Descubra empresas e órgãos que receberam verba.</p>
        </div>
      </Link>
    </div>
  </Card>
);

const EmendasParlamentar = () => {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await axios.get(`${API}/public/emendas/parlamentar/${pid}`, { timeout: 20000 });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Não foi possível carregar as emendas.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pid]);

  const share = async () => {
    const url = window.location.href;
    const nome = data?.parlamentar?.nome || 'Parlamentar';
    const text = `Perfil de ${nome} no Tô de Olho. Transparência pública com dados oficiais.`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Tô de Olho', text, url }); return; } catch (_) { /* fallback */ }
    }
    try {
      await navigator.clipboard?.writeText(`${text}\n${url}`);
      toast.success('Link copiado.');
    } catch (_) {
      toast.info(`Copie manualmente. ${url}`);
    }
  };

  if (loading) return <div className="text-center py-10 text-sm text-[#475569]" data-testid="emendas-parl-loading">Carregando emendas...</div>;

  if (error) return (
    <div className="max-w-3xl mx-auto py-10" data-testid="emendas-parl-error">
      <Card className="p-6 border border-[#FCA5A5] bg-[#FEE2E2]">
        <div className="flex items-start gap-2 text-[#991B1B]">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">Não foi possível carregar.</p>
            <p className="text-sm mt-1">{error}</p>
            <Button variant="outline" onClick={() => navigate('/emendas')} className="mt-3">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar para busca
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  if (!data) return null;

  const parl = data.parlamentar || {};
  const kpis = data.kpis || {};
  const dist = data.distribuicao_area || [];
  const topMuni = data.top_municipios || [];
  const topInst = data.top_instituicoes || [];
  const porAno = data.por_ano || [];
  const isUnavailable = data.data_source === 'unavailable';
  const totalReferencia = kpis.total_pago || kpis.total_empenhado || 0;
  const fonteExplique = 'Portal da Transparência';

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="emendas-parl-page">
      <button type="button" onClick={() => navigate('/emendas')} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#006030]" data-testid="emendas-parl-back">
        <ArrowLeft className="w-4 h-4" /> Voltar para busca
      </button>

      {/* Identity — sempre real via Câmara */}
      <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-parl-identity">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {parl.foto ? (
              <img src={parl.foto} alt={parl.nome} className="w-16 h-16 rounded-full object-cover border-2 border-[#E8F5E9] shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
                <User className="w-8 h-8 text-[#006030]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg md:text-2xl font-black text-[#0F1C2E] leading-tight break-words" data-testid="emendas-parl-nome">
                {parl.nome}
              </h1>
              <p className="text-xs text-[#475569] mt-1">
                {parl.cargo || 'Parlamentar'} · {parl.partido || '—'} · {parl.uf || '—'}
              </p>
              <p className="text-[10px] text-[#94A3B8] mt-1 uppercase tracking-wider">
                Identidade verificada · Fonte. Câmara dos Deputados
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap w-full sm:w-auto sm:self-start">
            <Button variant="outline" size="sm" onClick={share} className="border-[#006030] text-[#006030] hover:bg-[#E8F5E9] flex-1 sm:flex-none" data-testid="emendas-parl-share-btn">
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Compartilhar
            </Button>
          </div>
        </div>
      </Card>

      {/* Se dados de emendas nao disponiveis, mostra estado honesto + CTAs */}
      {isUnavailable && (
        <>
          <HonestEmptyState parl={parl} message={data.message} />
          <AlternativasCTA />
        </>
      )}

      {/* Se ha dados reais do Portal, renderiza dossie normal */}
      {!isUnavailable && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="emendas-parl-kpis">
            <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-total-destinado">
              <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
                <DollarSign className="w-3 h-3" /> Total destinado
              </div>
              <p className="text-lg md:text-xl font-black text-[#006030] mt-1">{brl(totalReferencia)}</p>
              <div className="mt-1.5">
                <ExplainButton
                  contexto={`total de emendas parlamentares destinadas por ${parl.nome}`}
                  valor={brl(totalReferencia)}
                  tipo="valor"
                  fonte={fonteExplique}
                  entidade={parl.nome}
                  testId="explain-total-destinado"
                />
              </div>
            </Card>
            <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-num-municipios">
              <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
                <MapPin className="w-3 h-3" /> Municípios
              </div>
              <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">{kpis.num_municipios || 0}</p>
            </Card>
            <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-num-instituicoes">
              <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
                <Building2 className="w-3 h-3" /> Instituições
              </div>
              <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">{kpis.num_instituicoes || 0}</p>
            </Card>
            <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-pct-executado">
              <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
                <TrendingUp className="w-3 h-3" /> Executado
              </div>
              <p className="text-lg md:text-xl font-black text-[#006030] mt-1">{kpis.pct_executado || 0}%</p>
            </Card>
          </div>

          {dist.length > 0 && (
            <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-parl-distribuicao">
              <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Distribuição por área</h2>
              <div className="space-y-2.5">
                {dist.map((d) => {
                  const meta = AREA_META[d.area] || AREA_META.outros;
                  const pct = totalReferencia ? Math.max(4, Math.round((d.valor / totalReferencia) * 100)) : 0;
                  return (
                    <div key={d.area} data-testid={`emendas-area-row-${d.area}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold text-[#0F1C2E]">{meta.emoji} {meta.label}</span>
                        <span className="font-bold text-[#006030]">{brl(d.valor)}</span>
                      </div>
                      <div className="h-2.5 bg-[#F1F5F9] rounded overflow-hidden">
                        <div className="h-full bg-[#006030]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {porAno.length > 0 && (
            <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-parl-timeline">
              <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Evolução por ano</h2>
              <div className="space-y-2">
                {porAno.map((p) => {
                  const maxVal = Math.max(...porAno.map((x) => x.empenhado));
                  const pctEmp = maxVal ? Math.max(6, Math.round((p.empenhado / maxVal) * 100)) : 0;
                  const pctPago = maxVal ? Math.round((p.pago / maxVal) * 100) : 0;
                  return (
                    <div key={p.ano} data-testid={`emendas-ano-${p.ano}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-semibold text-[#0F1C2E]">{p.ano}</span>
                        <span className="text-[#475569]">Empenhado {brl(p.empenhado)} · Pago {brl(p.pago)}</span>
                      </div>
                      <div className="relative h-3 bg-[#F1F5F9] rounded overflow-hidden">
                        <div className="absolute top-0 left-0 h-full bg-[#E2E8F0]" style={{ width: `${pctEmp}%` }} />
                        <div className="absolute top-0 left-0 h-full bg-[#006030]" style={{ width: `${pctPago}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {topMuni.length > 0 && (
            <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-parl-top-muni">
              <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Top 10 municípios beneficiados</h2>
              <div className="space-y-2">
                {topMuni.map((m, idx) => {
                  const canDrill = !!m.cod_ibge;
                  const row = (
                    <div className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0" data-testid={`emendas-muni-${idx}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#0F1C2E] truncate">{m.nome} <span className="text-[#94A3B8]">/ {m.uf}</span></p>
                        <p className="text-[11px] text-[#94A3B8]">{m.num_emendas} emenda(s)</p>
                      </div>
                      <span className="text-sm font-bold text-[#006030] shrink-0">{brl(m.valor)}</span>
                    </div>
                  );
                  return canDrill ? (
                    <Link
                      key={m.cod_ibge || idx}
                      to={`/emendas/municipio/${m.cod_ibge}`}
                      className="block hover:bg-[#F9FAFB] -mx-2 px-2 rounded"
                      data-testid={`emendas-muni-link-${idx}`}
                    >
                      {row}
                    </Link>
                  ) : (
                    <div key={idx}>{row}</div>
                  );
                })}
              </div>
            </Card>
          )}

          {topInst.length > 0 && (
            <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-parl-top-inst">
              <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Top 10 instituições beneficiadas</h2>
              <div className="space-y-2">
                {topInst.map((i, idx) => {
                  const hasCnpj = i.cnpj && String(i.cnpj).replace(/\D/g, '').length === 14;
                  const inner = (
                    <div className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#0F1C2E] truncate">{i.nome}</p>
                        <p className="text-[11px] text-[#94A3B8]">{i.tipo} · {i.num_emendas} emenda(s){hasCnpj ? '' : ' · CNPJ não informado'}</p>
                      </div>
                      <span className="text-sm font-bold text-[#006030] shrink-0">{brl(i.valor)}</span>
                    </div>
                  );
                  return hasCnpj ? (
                    <Link
                      key={i.cnpj || idx}
                      to={`/empresa/${i.cnpj}`}
                      className="block hover:bg-[#F9FAFB] -mx-2 px-2 rounded"
                      data-testid={`emendas-inst-${idx}`}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div key={idx} data-testid={`emendas-inst-${idx}`}>{inner}</div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      <Glossario />

      <ResponsibilityNotice
        source={isUnavailable ? 'Câmara dos Deputados (identidade)' : 'Portal da Transparência'}
        sourceUrl={isUnavailable ? 'https://dadosabertos.camara.leg.br' : 'https://portaldatransparencia.gov.br'}
      />

      <p className="text-[11px] text-[#94A3B8] text-center py-4">
        <FileText className="w-3 h-3 inline mr-1" />
        {isUnavailable
          ? 'Identidade verificada via Câmara dos Deputados. Dados de emendas dependem da chave do Portal da Transparência.'
          : 'Fonte oficial. Portal da Transparência do Governo Federal. Cache renovado a cada 7 dias.'}
      </p>
    </div>
  );
};

export default EmendasParlamentar;
